/**
 * embedded-web(iframe) 양방향 브릿지의 결과 반환 채널.
 *
 * iframe 콘텐츠(ably-webview-react)는 네이티브 브릿지 전역이 없으므로, 결과가 필요한 브릿지를
 * `{ type, body: { …, ok_function_name } }` 로 부모에 올린다. 호스트는 해당 화면을 열고,
 * 결과가 확정되면 **그 iframe 에게** 아래 프로토콜로 돌려준다.
 *
 *   { type: 'bridgeCallback', body: { callbackName, result } }
 *
 * `result` 는 네이티브 콜백에 넘어가는 payload 와 **동일한 문자열**이어야 한다. 콘텐츠는
 * 네이티브/웹 구분 없이 같은 파싱 코드를 쓴다(webview 의 `parseAddress` 등).
 *
 * 네이티브 대응: `bridge.deliverScreenResult` + `useScreenResult`.
 */
const CALLBACK_MESSAGE_TYPE = 'bridgeCallback';

interface PendingCallback {
  /** 결과를 돌려줄 iframe 의 contentWindow. */
  target: Window;
  /**
   * 그 iframe 의 오리진. 메시지를 수신한 시점의 `event.origin` 을 그대로 쓴다.
   * `'*'` 로 보내면 그 사이 iframe 이 다른 문서로 이동했을 때 결과가 엉뚱한 오리진에 노출된다.
   */
  targetOrigin: string;
}

const pendingCallbacks = new Map<string, PendingCallback>();

/**
 * iframe 이 결과를 기다리고 있다고 등록한다. 콜백 이름은 콘텐츠가 만든 1회용 값이다.
 */
export const registerHostBridgeCallback = (
  callbackName: string,
  target: Window,
  targetOrigin: string,
): void => {
  pendingCallbacks.set(callbackName, { target, targetOrigin });
};

/**
 * 등록된 iframe 에 결과를 돌려준다.
 *
 * @returns 전달 대상이 있었는지. 대상이 없으면(네이티브 진입, 이미 응답, iframe 파기) false.
 */
export const resolveHostBridgeCallback = (
  callbackName: string,
  result: unknown,
): boolean => {
  const pending = pendingCallbacks.get(callbackName);
  if (pending == null) return false;

  // 1회용이다. 화면이 결과를 두 번 발사해도 두 번 전달되지 않도록 먼저 제거한다.
  pendingCallbacks.delete(callbackName);

  pending.target.postMessage(
    JSON.stringify({
      type: CALLBACK_MESSAGE_TYPE,
      body: { callbackName, result: JSON.stringify(result) },
    }),
    pending.targetOrigin,
  );

  return true;
};